<template>
  <div class="vx-card p-6">
    <div ref="connectToGoogleSection">
      <h2 class="mb-5">
        {{ hasTenantSquareProvider ? $t('ConnectedAccountTitle') : $t('ConnectYourAccountTitle') }}
      </h2>

      <div
        v-if="hasTenantSquareProvider"
        class="flex flex-wrap justify-between items-center mb-base">
        <div>
          <p>
            {{ $t('ConnectedWithSquareMsg') }}
          </p>
          <p class="font-medium mb-4">{{ tenantSquareProvider.businessName }}</p>
        </div>
        <vs-button
          type="border"
          color="danger"
          @click="confirmDisconnectGoogle()">
          {{ $t('$General.Disconnect') }}
        </vs-button>
      </div>

      <div
        v-else
        class="mb-base">
        <vs-button
          icon-pack="feather"
          icon="icon icon-settings"
          @click="connectSquareHandler()">
          {{ $t('ConnectToSquare') }}
        </vs-button>

        <span
          v-if="squareConnectionError"
          class="text-danger">{{ squareConnectionError }}.</span>
      </div>

      <vs-divider/>

      <div class="mt-3">
        <p>
          {{
            hasTenantSquareProvider
              ? $t('ConnectedAccountMsg')
              : $t('ConnectYourAccountMsg')
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import configApi from '@/api/config';

export default {
  name: 'SquareIntegrationConfig',
  i18n: {
    messages: {
      en: {
        ConnectYourAccountTitle: 'Connect your Square account',
        ConnectYourAccountMsg: 'Link your Square account to automatically sync your Square contacts with GrapePerks.',
        ConnectedAccountTitle: 'Connected account',
        ConnectedAccountMsg: 'Your Square contacts are now being automatically synchronized with GrapePerks.',
        ConnectToSquare: 'Connect to Square',
        ConnectedWithSquareMsg: 'Your account is successfully connected to Square.',
        ConnectedToSquareNotificationTitle: 'Square connection',
        ConnectedToSquareNotificationMsg: 'Successfully connected to Square',
        ConnectedToSquareNotificationErrorTitle: 'Square connection error',
        ConnectedToSquareNotificationErrorMsg: 'Failed to connect account',
        DisconnectedToSquareNotificationTitle: 'Square disconnection',
        DisconnectedToSquareNotificationMsg: 'Successfully disconnected from Square',
        DisconnectSquareAccountConfirmDialog: {
          Title: 'Disconnect Square account?',
          Msg: 'Disconnecting your account will stop the automatic synchronization of your Square contacts with GrapePerks.',
        },
      },
    },
  },
  data() {
    return {
      squareConnectionError: '',
    };
  },
  computed: {
    ...mapGetters({
      hasTenantSquareProvider: 'auth/hasTenantSquareProvider',
      tenantSquareProvider: 'auth/tenantSquareProvider',
    }),
    ...mapState({
      authUser: (state) => state.auth.user,
      tokenJwt: (state) => state.auth.token,
    }),
  },
  created() {
    this.verifyQueryParamSquareAccount();
  },
  mounted() {
    this.scrollIntoConnectionSquareError();
  },
  methods: {
    ...mapActions({
      fetchTenantOfUser: 'auth/fetchTenantOfUser',
      disconnectToSquare: 'auth/disconnectToSquare',
      refreshToken: 'auth/refreshToken',
    }),
    ...mapMutations({
      partialUpdateAuthUser: 'auth/PARTIAL_UPDATE_AUTH_USER',
    }),
    async connectSquareHandler() {
      if (!this.tokenJwt) {
        await this.refreshToken();
      }

      window.location.href = `${configApi.url}/tenants/connect-square-account/${this.tokenJwt}`;
    },
    async verifyQueryParamSquareAccount() {
      if (this.$route.query.connectedSquare) {
        this.$vs.loading({ type: 'radius' });
        await this.fetchTenantOfUser();
        this.$vs.loading.close();

        this.$showSuccessActionNotification({
          title: this.$t('ConnectedToSquareNotificationTitle'),
          text: this.$t('ConnectedToSquareNotificationMsg'),
        });

        const url = new URL(window.location.href);
        url.searchParams.delete('connectedSquare');
        window.history.replaceState({}, '', url);
        this.$route.query.connectedSquare = '';
        this.scrollIntoConnectionSquare();
      } else if (this.$route.query.connectedSquareError) {
        this.$showGeneralErrorNotification({
          title: this.$t('ConnectedToSquareNotificationErrorTitle'),
          text: this.$t('ConnectedToSquareNotificationErrorMsg'),
        });

        this.squareConnectionError = this.$route.query.connectedSquareError;

        const url = new URL(window.location.href);
        url.searchParams.delete('connectedSquareError');
        window.history.replaceState({}, '', url);
        this.$route.query.connectedSquareError = '';
      }
    },
    confirmDisconnectGoogle() {
      this.$showConfirmDeleteDialog({
        title: this.$t('DisconnectSquareAccountConfirmDialog.Title'),
        text: this.$t('DisconnectSquareAccountConfirmDialog.Msg'),
        accept: () => this.disconnectSquareHandler(),
        acceptText: this.$t('$General.Disconnect'),
        cancelText: this.$t('$General.Cancel'),
      });
    },
    async disconnectSquareHandler() {
      this.$vs.loading({ type: 'radius' });
      await this.disconnectToSquare();
      this.$vs.loading.close();

      this.$showSuccessActionNotification({
        title: this.$t('DisconnectedToSquareNotificationTitle'),
        text: this.$t('DisconnectedToSquareNotificationMsg'),
      });
    },
    scrollIntoConnectionSquareError() {
      if (this.squareConnectionError) {
        this.scrollIntoConnectionSquare();
      }
    },
    scrollIntoConnectionSquare() {
      setTimeout(() => {
        const el = this.$refs.connectToGoogleSection;

        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    },
  },
};
</script>

<style scoped lang="scss">

</style>
